// ============================================================
// format.js
// ------------------------------------------------------------
// ฟังก์ชันแปลง/จัดรูปแบบข้อมูล ที่ใช้ร่วมกันทุกหน้าจอ
// (ตัวเลข, วันที่ไทย, การเรียงข้อมูล, รูปภาพ base64)
//
// ตรรกะทุกตัวยกมาจาก CustomComponent.js เดิมแบบไม่แก้ผลลัพธ์
// ============================================================

// เติมเลข 0 ข้างหน้าให้ครบจำนวนหลัก เช่น NumberZero(5) → "05", NumberZero(2568, 4) → "2568"
export const NumberZero = (number, length) => (length ? number.toString().padStart(length, "0") : number.toString().padStart(2, "0"));

// ปี พ.ศ. สำหรับแสดงผล (backend เก็บ/รับเป็น ค.ศ. เสมอ แปลงที่ชั้นแสดงผลเท่านั้น)
export const toBuddhistYear = (year) => Number(year || 0) + 543;

// ชื่องวดแบบอ่านง่าย เช่น periodLabel(2026, 8) → "08/2569"
export const periodLabel = (year, month) => `${String(month).padStart(2, "0")}/${toBuddhistYear(year)}`;

const MONTH_SHORT = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
const MONTH_FULL = ["มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"];

// จัดรูปแบบวันที่ — format_date(date, format, locale)
//   format  : "dd/mm/yyyy", "d mmmm yyyy", "dd/mm/yyyy hh:mm" ฯลฯ (ถ้ามีคำว่า "date" จะคืน Date object)
//   locale  : "en" = ปี ค.ศ. | ไม่ส่ง/อย่างอื่น = ปี พ.ศ.
//   ไม่ส่ง format → ได้ "dd/mm/yyyy" (พ.ศ.)
// หมายเหตุ: เช็ค getFullYear() > 2100 เพื่อกันเคสที่ข้อมูลจาก DB เป็น พ.ศ. มาแล้ว (จะได้ไม่บวก 543 ซ้ำ)
export const format_date = (date, format, lacale) => {
  const new_date = new Date(date);
  const day = new_date.getDate();
  const month = new_date.getMonth();
  const hour = new_date.getHours();
  const minute = new_date.getMinutes();
  const second = new_date.getSeconds();

  // ตัดสินใจว่าปีที่จะแสดงเป็น ค.ศ. หรือ พ.ศ.
  let year;
  if (lacale && lacale.toLowerCase() === "en") {
    year = new_date.getFullYear() > 2100 ? new_date.getFullYear() - 543 : new_date.getFullYear();
  } else {
    year = new_date.getFullYear() > 2100 ? new_date.getFullYear() : new_date.getFullYear() + 543;
  }

  // ไม่ระบุ format → รูปแบบมาตรฐาน dd/mm/yyyy
  if (!format) return NumberZero(day) + "/" + NumberZero(month + 1) + "/" + NumberZero(year, 4);

  // ขอเป็น Date object
  if (format.includes("date")) return new_date;

  format = format.toLowerCase();

  // เวลา
  if (format.includes("hh:mm:ss")) {
    format = format.replaceAll("hh:mm:ss", NumberZero(hour) + ":" + NumberZero(minute) + ":" + NumberZero(second));
  } else if (format.includes("hh:mm")) {
    format = format.replaceAll("hh:mm", NumberZero(hour) + ":" + NumberZero(minute));
  }
  // วัน — dd = เติม 0, d = ไม่เติม
  if (format.includes("dd")) format = format.replaceAll("dd", NumberZero(day));
  else format = format.replaceAll("d", day);
  // เดือน — mmmm = ชื่อเต็ม, mmm = ชื่อย่อ, mm = ตัวเลขเติม 0, m = ตัวเลขเปล่า
  if (format.includes("mmmm")) format = format.replaceAll("mmmm", MONTH_FULL[month]);
  else if (format.includes("mmm")) format = format.replaceAll("mmm", MONTH_SHORT[month]);
  else if (format.includes("mm")) format = format.replaceAll("mm", NumberZero(month + 1));
  else if (format.includes("m")) format = format.replaceAll("m", month + 1);
  // ปี — yyyy = 4 หลัก, yyy/yy = 2 หลักท้าย, y = ตามจริง
  if (format.includes("yyyy")) format = format.replaceAll("yyyy", NumberZero(year, 4));
  else if (format.includes("yyy")) format = format.replaceAll("yyy", year.toString().slice(-2));
  else if (format.includes("yy")) format = format.replaceAll("yy", year.toString().slice(-2));
  else if (format.includes("y")) format = format.replaceAll("y", year);

  return format;
};

// จัดรูปแบบเงิน — ทศนิยม 2 ตำแหน่ง + คั่นหลักพันด้วย comma เช่น 1234.5 → "1,234.50"
// ค่าว่าง / 0 / null → "0.00"
//
// ปัดด้วย roundTo (ปัดครึ่งขึ้น) ก่อนแสดงผล ให้ตรงกับตัวเลขที่ระบบใช้คำนวณจริง
// (Number.toFixed ของ JavaScript เพี้ยนในเคส .xx5 เช่น (1.005).toFixed(2) = "1.00")
export const toFixed = (number, size) => {
  const digits = size || 2;
  // NOTE เดิมคืน "0.00" ตายตัว ไม่สนใจ size — toFixed(0, 3) จึงได้ "0.00" ไม่ใช่ "0.000"
  if (!number) return (0).toFixed(digits);
  return roundTo(number, digits)
    .toFixed(digits)
    .replace(/\d(?=(\d{3})+\.)/g, "$&,");
};

// รวมยอดใน array — total(array) รวมค่าตัวเลขตรง ๆ | total(array, "amount") รวมตาม key
export function total(array, key) {
  if (key) return array.reduce((a, b) => a + (b[key] || 0), 0);
  return array.reduce((a, b) => a + b, 0);
}

// ★ การปัดเศษของทั้งระบบใช้ที่นี่ที่เดียว — "ปัดครึ่งขึ้น" (ROUND_HALF_UP)
//   ให้ผลตรงกับ backend ที่ใช้ Decimal(str(value)).quantize(Decimal("0.01"), rounding=ROUND_HALF_UP)
//   33.334 -> 33.33 | 33.335 -> 33.34 | -33.335 -> -33.34 (ครึ่งปัดออกจากศูนย์)
//
// ทำไมต้องปัดจาก "สตริง" ไม่ใช่คูณ 100 แล้วปัด
//   ทศนิยมลอยตัวคูณแล้วเพี้ยน เช่น
//     Math.round(47.324999999999996 * 100)  ->  4733  (ควรได้ 4732)
//     เพราะ 47.324999999999996 * 100 ปัดเป็น double ได้ 4732.5 พอดี
//   String(x) ของ JavaScript ให้ตัวเลขสั้นที่สุดที่แทนค่านั้นได้ ซึ่งเป็นชุดเดียวกับ str(x) ของ Python
//   จึงตัดสินใจจากหลักถัดไปในสตริงตรง ๆ ผลจะตรงกับ backend ทุกกรณี
export function roundTo(num, digits = 2) {
  if (!num) return 0;
  const value = Number(num);
  if (!isFinite(value)) return 0;

  const sign = value < 0 ? -1 : 1;
  let text = String(Math.abs(value));

  // เลขที่ JavaScript เขียนเป็น exponent (เช่น 1e-7) แปลงเป็นทศนิยมธรรมดาก่อน
  if (text.includes("e") || text.includes("E")) text = Math.abs(value).toFixed(Math.max(digits + 1, 20));

  const [intPart, fracPart = ""] = text.split(".");
  if (fracPart.length <= digits) return value;

  // ตัดให้เหลือทศนิยมตามที่ต้องการ แล้วดูหลักถัดไป — ตั้งแต่ 5 ขึ้นไปปัดขึ้น
  const kept = fracPart.slice(0, digits);
  const nextDigit = Number(fracPart[digits]);
  let scaled = Number(intPart + kept);
  if (nextDigit >= 5) scaled += 1;

  return sign * (scaled / Math.pow(10, digits));
}

// ปัดเป็นทศนิยม 2 ตำแหน่ง (ตัวที่หน้าจอส่วนใหญ่เรียกใช้)
export function roundToTwo(num) {
  return roundTo(num, 2);
}

// ตัวเรียงข้อมูลหลาย key — array.sort(fieldSorter(["address_number", "-datetime_create"]))
// ใส่ "-" หน้า key เพื่อเรียงจากมากไปน้อย
export function fieldSorter(fields) {
  return function (a, b) {
    return fields
      .map(function (o) {
        let dir = 1;
        if (o[0] === "-") {
          dir = -1;
          o = o.substring(1);
        }
        if (a[o] > b[o]) return dir;
        if (a[o] < b[o]) return -dir;
        return 0;
      })
      .reduce((p, n) => p || n, 0);
  };
}

// เตรียมค่า src ให้ <img> — รับได้ทั้ง base64 เปล่า, data URI เต็ม, หรือ http url
export function base64(img) {
  return img.includes("base64") ? img : img.includes("http") ? img : "data:image/png;base64," + img;
}

// เอาเฉพาะตัวเลขจากข้อความ (ใช้กับช่องเบอร์โทร/เลขบัตร)
export function number(num) {
  return num.replace(/\D/g, "");
}

// เช็คว่าข้อความที่พิมพ์ยังเป็นตัวเลขทศนิยมที่รับได้ไหม
// float(value)    → ทศนิยมไม่เกิน 2 ตำแหน่ง
// float(value, 3) → ทศนิยมไม่เกิน 3 ตำแหน่ง (ใช้กับช่องปรับเศษสตางค์/เพิ่ม-ลดหนี้ ในหน้ารับชำระ)
export function float(num, count) {
  if (count === 3 && /^\d*\.?\d{0,3}$/.test(num)) return true;
  if (/^\d*\.?\d{0,2}$/.test(num)) return true;
  return false;
}

// สุ่มรหัสผ่าน (ใช้ตอนสร้างผู้ใช้ใหม่ / รีเซ็ตรหัส)
export async function gen_password(count) {
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < count; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
}
